import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked
} from "@angular/core";

import { LktIconComponent } from "../icon/icon.component";
import { LktNotificationItem } from "../../types/notification-item.type";

/**
 * Piso de retomada, de `js/notificacoes.js:837` (`Math.max(600, restante)`).
 * Existe para o toast não fechar no MESMO instante em que a pessoa solta o
 * hover/foco — 600ms de folga perceptível antes do fechamento automático.
 * Consequência aceita: a vida total do toast pode passar de `lifeMs()`
 * quando a pausa acontece perto do fim. Aplicado a QUALQUER retomada — por
 * mouse/foco ou pela preferência de movimento voltando a permitir animação —
 * porque a unificação (round 4) trata as duas como a mesma fonte de pausa.
 */
const MIN_RESUME_MS = 600;

/**
 * Aviso temporário de notificação (DEV-872, CA 04, CA 05 e CA 08).
 *
 * O toast é dono do PRÓPRIO ciclo de vida: ele agenda o fim, pausa, retoma e
 * avisa por `expired`. Quem remove do DOM é o container, que é o único a
 * conhecer a fila e o teto de três vivos — daí `expired`/`closeToast` serem
 * saídas e não `this.destroy()`.
 *
 * `animate() === false` é movimento reduzido: sem animação, sem barra de vida e
 * **sem timer**. Some o indicador de tempo, some o fechamento automático — um
 * aviso que desaparece sem sinal é pior que um aviso que fica. Isso é
 * comportamento, não estilo, por isso não vive só no `@media` do SCSS.
 *
 * A pausa é por CONTADOR, não por booleano: cursor e foco de teclado entram e
 * saem em ordens diferentes, e um `mouseleave` não pode retomar o relógio
 * enquanto o foco ainda está dentro do cartão.
 */
@Component({
  selector: "lkt-notification-toast",
  standalone: true,
  imports: [LktIconComponent],
  templateUrl: "./notification-toast.component.html",
  styleUrl: "./notification-toast.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[attr.data-cy]": "dataCy() || null",
    "[class.nt-toast--neutral]": "item().tone === 'neutral'",
    "[class.nt-toast--success]": "item().tone === 'success'",
    "[class.nt-toast--warning]": "item().tone === 'warning'",
    "[class.nt-toast--negative]": "item().tone === 'negative'",
    "(mouseenter)": "onPause()",
    "(mouseleave)": "onResume()",
    "(focusin)": "onPause()",
    "(focusout)": "onResume()"
  }
})
export class LktNotificationToastComponent {
  public readonly item = input.required<LktNotificationItem>();
  /** Vida útil em ms. Alimenta o timeout e o `--nt-vida` da barra. */
  public readonly lifeMs = input<number>(10000);
  /** `false` = movimento reduzido: sem animação, sem barra e sem auto-fechar. */
  public readonly animate = input<boolean>(true);
  public readonly dataCy = input<string>("");

  /** A vida terminou sozinha. O container remove da fila. */
  public readonly expired = output<void>();
  /** Nome não é `close` porque `@angular-eslint/no-output-native` é `error`. */
  public readonly closeToast = output<void>();
  public readonly ctaSelect = output<void>();

  public readonly closeDataCy = computed<string>(() => `${this.dataCy()}-close`);
  public readonly ctaDataCy = computed<string>(() => `${this.dataCy()}-cta`);

  /** Profundidade da pausa: 0 = correndo. Cobre a sobreposição mouse × foco. */
  private readonly pauseCount = signal<number>(0);
  private readonly lifeHandle = signal<ReturnType<typeof globalThis.setTimeout> | null>(null);
  /**
   * Quanto sobra da vida. Recalculado a cada pausa, nunca reiniciado do zero. `null` = nunca
   * chegou a agendar (nasceu com `animate=false`) — sentinela explícito desde o round 6, no lugar
   * do antigo `remaining > 0`, que confundia "nunca agendou" com "restante não positivo": os dois
   * caíam no mesmo `Math.max`/fallback, mas por razões diferentes, e um sentinela só serve se
   * distinguir os casos que importa distinguir.
   */
  private readonly remainingMs = signal<number | null>(null);
  private readonly startedAt = signal<number>(0);
  /** Uma vez encerrado (expirou, fechou ou agiu), este toast não volta a agendar nada. */
  private readonly finished = signal<boolean>(false);

  /**
   * O timer corre quando ninguém está pausando E a animação está ligada. Tratar `animate` como
   * uma fonte de pausa — e não como um caminho paralelo de gestão do timer — é o que mantém a
   * contabilidade do tempo decorrido num lugar só. Os achados 5, 6 e 7 da revisão vieram de
   * haver duas: um `effect` cuidando de `animate` e rotinas de pausa cuidando de mouse/foco, cada
   * uma mexendo na sua metade do estado (`pauseCount` vs. `remainingMs`/`lifeHandle`).
   */
  private readonly shouldRun = computed<boolean>(
    () => !this.finished() && this.animate() && this.pauseCount() === 0
  );

  /**
   * SNAPSHOT da duração que `syncTimer` agendou (ou agendaria) no instante em que a barra MONTA —
   * não um valor vivo. `source: animate` porque é exatamente quando o `@if (animate())` do
   * template (re)cria o `<span>`: religar a preferência de movimento no meio de uma pausa precisa
   * de uma NOVA duração (achado round 5, aberto 2, corrigido abaixo). Mas um hover comum
   * (mouse/foco) NÃO muda `animate` — muda só `remainingMs`, lido em `untracked()` para não virar
   * dependência — e por isso não recomputa `lifeBarMs`.
   *
   * Achado carregado da Task 13 para a Task 16 (round 6): antes, `lifeBarMs` era um `computed`
   * vivo ligado a `remainingMs()`, então QUALQUER pausa recalculava `--nt-vida` sob o `<span>` que
   * o CSS já está pausando via `:hover`/`:focus-within` (`.scss:144-147`). Trocar a
   * `animation-duration` de uma animação CSS em andamento recalcula o progresso como
   * tempoLocal/novaDuração (verificado em Chrome 151 real headless, não só por leitura de spec —
   * ver relatório): 4000ms de 10000 (40% consumidos) sob nova duração 6000 vira ~66,7%
   * consumidos, e um hover depois da metade da vida zera a barra na hora. Antes do round 4 (que
   * introduziu a religação por `remainingMs`) isso era certo por construção, porque a duração era
   * sempre `lifeMs()` fixo; o round 4 trocou uma mentira rara (mudar a preferência de movimento
   * com o toast na tela) por uma comum (todo hover depois da metade da vida). Consumida por
   * `--nt-vida`.
   */
  public readonly lifeBarMs = linkedSignal<boolean, number>({
    source: this.animate,
    computation: (): number =>
      untracked((): number => {
        const remaining = this.remainingMs();
        return Math.max(
          MIN_RESUME_MS,
          remaining !== null && remaining > 0 ? remaining : this.lifeMs()
        );
      })
  });

  constructor() {
    inject(DestroyRef).onDestroy((): void => this.clearLife());

    // `effect` cobre a mudança de `animate`/`finished` vinda de FORA (input): não há handler de
    // evento para isso, então a reatividade do Angular é o único jeito de saber. `onPause`/
    // `onResume` chamam a MESMA `syncTimer` de forma síncrona (abaixo) — um `effect` sozinho não
    // é confiável aqui: só roda de fato num flush de change detection, e um `dispatchEvent` de
    // mouse/foco sem um `detectChanges()` no meio (o caso comum em produção E nos testes deste
    // arquivo) não gera esse flush. `syncTimer` existir como função única, chamada dos dois
    // lugares, é o que fecha a classe de defeito dos achados 5/6/7: só ELA mexe na contabilidade
    // de tempo decorrido, não importa quem a disparou.
    effect((): void => this.syncTimer());
  }

  public onPause(): void {
    /* O contador é sempre exato, com ou sem animação: condicioná-lo ao `animate` (como antes)
       o deixava preso quando a preferência mudava no meio de um hover (achado 5). */
    this.pauseCount.update((count) => count + 1);
    this.syncTimer();
  }

  public onResume(): void {
    /* Math.max(0, ...) protege contra um mouseleave/focusout órfão (sem o enter/in
       correspondente já contado). */
    this.pauseCount.update((count) => Math.max(0, count - 1));
    this.syncTimer();
  }

  /**
   * Único lugar que liga/desliga o timer e mexe na contabilidade de tempo decorrido
   * (`remainingMs`/`startedAt`/`lifeHandle`), chamado tanto pelo `effect` (mudança de
   * `animate`/`finished`) quanto diretamente por `onPause`/`onResume` (mudança de `pauseCount`).
   * Substitui o `effect` de `animate` (achado 1) sozinho e as rotinas `pauseLife`/`resumeLife`
   * que existiam antes, cada uma mexendo na sua metade do estado.
   */
  private syncTimer(): void {
    const shouldRun = this.shouldRun();
    const running = this.lifeHandle() !== null;
    if (shouldRun === running) {
      return;
    }
    if (shouldRun) {
      // `remainingMs` só é preenchido por um agendamento anterior — `null` (nunca agendou, achado
      // 6) e restante zero/negativo caem no mesmo fallback de vida inteira, por razões diferentes.
      // O piso `MIN_RESUME_MS` (achado 3) garante folga mínima em qualquer retomada.
      const remaining = this.remainingMs();
      this.scheduleLife(
        Math.max(MIN_RESUME_MS, remaining !== null && remaining > 0 ? remaining : this.lifeMs())
      );
      return;
    }
    this.captureRemaining();
    this.clearLife();
  }

  /**
   * Congela o restante pelo tempo já decorrido. Chamada por `syncTimer` em QUALQUER transição
   * para "não deve correr" (pausa por mouse/foco OU `animate` caindo) — sem gate de `animate`,
   * porque congelar é contabilidade, não animação. Sem timer rodando (`lifeHandle() === null`)
   * não há o que congelar: evita subtrair o decorrido duas vezes.
   */
  private captureRemaining(): void {
    const remaining = this.remainingMs();
    // `remaining === null` aqui é guarda DEFENSIVA, não um caminho alcançável hoje:
    // `lifeHandle()` só fica não-nulo dentro de `scheduleLife`, que grava `remainingMs` (nunca
    // null) ANTES de gravar o handle — então `lifeHandle() !== null` já implica `remaining !==
    // null`. Mantida mesmo assim para não depender de detalhe de ordem entre os dois `set`, e
    // para o próximo leitor não caçar um caminho que não existe.
    if (this.lifeHandle() === null || remaining === null) {
      return;
    }
    this.remainingMs.set(remaining - (Date.now() - this.startedAt()));
  }

  public onClose(): void {
    this.finished.set(true);
    this.clearLife();
    this.closeToast.emit();
  }

  /** A ação fecha o toast E marca como lida — quem marca é o container. */
  public onCta(): void {
    this.finished.set(true);
    this.clearLife();
    this.ctaSelect.emit();
  }

  private scheduleLife(ms: number): void {
    if (this.finished()) {
      return;
    }
    /* Defensivo: cancela qualquer handle anterior antes de agendar um novo. Sem o `ngOnInit`
       chamando isto diretamente, hoje nada chama `scheduleLife` duas vezes seguidas sem passar
       por `clearLife` — mas essa inocuidade não pode depender de detalhe de framework (a ordem
       dos pre-order hooks perante os effects). Correto independente de haver ou não um caminho
       que a exercite hoje. */
    this.clearLife();
    this.remainingMs.set(ms);
    this.startedAt.set(Date.now());
    this.lifeHandle.set(globalThis.setTimeout((): void => this.onLifeEnd(), ms));
  }

  private onLifeEnd(): void {
    /* Defensivo: se `finished` já está true (não deveria, mas não pode depender de detalhe de
       framework para garantir isso), não emite `expired` de novo. */
    if (this.finished()) {
      return;
    }
    this.lifeHandle.set(null);
    this.finished.set(true);
    this.expired.emit();
  }

  private clearLife(): void {
    const handle = this.lifeHandle();
    if (handle !== null) {
      globalThis.clearTimeout(handle);
      this.lifeHandle.set(null);
    }
  }
}
